<template>
  <div
    :class="[
      'relative overflow-hidden shrink-0',
      sizeClass,
      props.size !== 'fill' && !props.rounded ? 'rounded-full' : '',
      props.rounded ? 'rounded-full' : '',
    ]"
    role="img"
    :aria-label="`Avatar for ${props.candidateName}`"
    @error.capture="imageError = true"
  >
    <LazyImage v-if="!imageError" :src="imageUrl" :title="props.candidateName" />
    <div
      v-else
      class="w-full h-full flex items-center justify-center bg-surface-container-high"
      role="img"
      :aria-label="`${props.candidateName} (no image available)`"
    >
      <i class="ibi-cloud-error-filled text-surface-variant" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { media } from '@ibgroup-tws-webapps/ratio.ui';
import LazyImage from './LazyImage.vue';
import type { PartyCode } from '@/electionHub/types/election.types';
import { getPartyAvatarUrl, isPartyStrikeName } from '@/electionHub/utils/partyUtils';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl' | 'fill';

const SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'w-7 h-7',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-24 h-24',
  fill: 'w-full h-full',
};

const props = withDefaults(
  defineProps<{
    conid: number;
    candidateName: string;
    size?: AvatarSize;
    rounded?: boolean;
    /** When set to a major party code (DEM/GOP), shows the party logo instead of the conid-based image */
    partyCode?: PartyCode;
  }>(),
  {
    size: 'md',
    rounded: false,
    partyCode: undefined,
  },
);

const imageError = ref(false);

const imageUrl = computed(() => {
  // Show party logo only when the strike IS a party name (general elections).
  // Named candidates (primaries) fall through to the conid-based photo lookup.
  if (props.partyCode && isPartyStrikeName(props.candidateName)) {
    const partyUrl = getPartyAvatarUrl(props.partyCode, media.scheme === 'dark');
    if (partyUrl) return partyUrl;
  }
  return `/tws.proxy/public/icons/forecastx?conid=${props.conid}&fallback=true`;
});

const sizeClass = computed(() => SIZE_CLASSES[props.size]);
</script>
